import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';

const AdminUsers = () => {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [deleteModal, setDeleteModal] = useState(null);
  const [deleting, setDeleting]       = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(null);
  const { success, error }            = useToast();
  const { currentUser }               = useAuth();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/users');
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [error]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filteredUsers = users.filter(u =>
    !search.trim() ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  const handleToggleAdmin = async (user) => {
    if (user.uid === currentUser?.uid) { error("You can't modify your own admin status"); return; }
    setTogglingAdmin(user.uid);
    try {
      await api.post('/admin/set-admin', { uid: user.uid, isAdmin: !user.isAdmin });
      success(`Admin ${!user.isAdmin ? 'granted to' : 'revoked from'} ${user.email}`);
      fetchUsers();
    } catch (err) {
      error('Failed to update admin status');
    } finally {
      setTogglingAdmin(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    if (deleteModal.uid === currentUser?.uid) { error("You can't delete your own account"); return; }
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${deleteModal.uid}`);
      success('User deleted successfully');
      setDeleteModal(null);
      fetchUsers();
    } catch (err) {
      error('Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <AdminLayout title="User Management">
      <div className="au-header">
        <input
          type="text" className="form-input"
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: '300px' }}
        />
        <span className="au-count">{filteredUsers.length} users</span>
      </div>

      <div className="au-banner">
        <span>👑</span>
        <div>
          <strong>Admin Management</strong> — Toggle admin access for users.
          Admins can create posts, manage profiles, and access this panel.
          You cannot modify your own account.
        </div>
      </div>

      <div className="admin-card">
        {loading ? (
          <div className="au-center">Loading users...</div>
        ) : filteredUsers.length > 0 ? (
          <div className="au-table-wrap">
            <table className="au-table">
              <thead>
                <tr><th>User</th><th>Joined</th><th>Last Login</th><th>Role</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.uid} className={user.uid === currentUser?.uid ? 'au-me' : ''}>
                    <td>
                      <div className="au-user">
                        <div className="au-avatar">
                          {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="au-name">
                            {user.displayName || 'No name'}
                            {user.uid === currentUser?.uid && <span className="au-you">You</span>}
                          </div>
                          <div className="au-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="au-date">{formatDate(user.createdAt)}</td>
                    <td className="au-date">{formatDate(user.lastLogin)}</td>
                    <td>
                      {user.isAdmin
                        ? <span className="role role--admin">👑 Admin</span>
                        : <span className="role role--user">👤 User</span>
                      }
                    </td>
                    <td>
                      <div className="au-actions">
                        <button
                          className={`au-btn ${user.isAdmin ? 'au-btn--revoke' : 'au-btn--grant'}`}
                          onClick={() => handleToggleAdmin(user)}
                          disabled={togglingAdmin === user.uid || user.uid === currentUser?.uid}
                        >
                          {togglingAdmin === user.uid ? '...' : user.isAdmin ? '🚫 Revoke' : '👑 Make Admin'}
                        </button>
                        <button
                          className="au-btn au-btn--delete"
                          onClick={() => setDeleteModal(user)}
                          disabled={user.uid === currentUser?.uid}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="au-center">
            <div style={{ fontSize: '3rem', marginBottom: '8px' }}>👥</div>
            <p>No users found.</p>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Permanently delete "${deleteModal?.email}"? They won't be able to log in.`}
        confirmText="Delete User"
        type="danger"
        loading={deleting}
      />

      <style jsx>{`
        .au-header { display:flex; align-items:center; gap:var(--space-4); margin-bottom:var(--space-4); flex-wrap:wrap; }
        .au-count { color:var(--neutral-600); font-size:var(--text-sm); margin-left:auto; }
        .au-banner { display:flex; align-items:flex-start; gap:var(--space-3); background:var(--gold-100); border:1px solid var(--gold-300); border-radius:var(--radius-lg); padding:var(--space-4); margin-bottom:var(--space-6); font-size:var(--text-sm); color:var(--gold-900); line-height:var(--leading-relaxed); }
        .au-banner span { font-size:1.3rem; flex-shrink:0; }
        .au-banner strong { display:block; margin-bottom:var(--space-1); }
        .admin-card { background:white; border-radius:var(--radius-xl); padding:var(--space-6); border:1px solid var(--neutral-200); box-shadow:var(--shadow-sm); }
        .au-table-wrap { overflow-x:auto; }
        .au-table { width:100%; border-collapse:collapse; font-size:var(--text-sm); }
        .au-table th { text-align:left; padding:var(--space-3) var(--space-4); background:var(--neutral-50); color:var(--neutral-700); font-weight:var(--weight-semibold); border-bottom:2px solid var(--neutral-200); font-size:var(--text-xs); text-transform:uppercase; letter-spacing:0.5px; }
        .au-table td { padding:var(--space-4); border-bottom:1px solid var(--neutral-200); vertical-align:middle; }
        .au-table tr:last-child td { border-bottom:none; }
        .au-table tr:hover td { background:var(--neutral-50); }
        .au-me td { background:var(--primary-50) !important; }
        .au-user { display:flex; align-items:center; gap:var(--space-3); }
        .au-avatar { width:38px; height:38px; border-radius:50%; background:linear-gradient(135deg,var(--primary-500),var(--gold-500)); color:white; display:flex; align-items:center; justify-content:center; font-weight:var(--weight-bold); font-size:var(--text-sm); flex-shrink:0; }
        .au-name { font-weight:var(--weight-semibold); color:var(--neutral-900); display:flex; align-items:center; gap:var(--space-2); }
        .au-email { font-size:var(--text-xs); color:var(--neutral-500); }
        .au-you { font-size:10px; background:var(--primary-100); color:var(--primary-600); padding:1px var(--space-2); border-radius:var(--radius-full); font-weight:var(--weight-bold); }
        .au-date { font-size:var(--text-xs); color:var(--neutral-500); white-space:nowrap; }
        .role { padding:var(--space-1) var(--space-3); border-radius:var(--radius-full); font-size:var(--text-xs); font-weight:var(--weight-semibold); }
        .role--admin { background:var(--gold-100); color:var(--gold-900); }
        .role--user  { background:var(--neutral-100); color:var(--neutral-600); }
        .au-actions { display:flex; gap:var(--space-2); flex-wrap:wrap; }
        .au-btn { padding:var(--space-1) var(--space-3); border:none; border-radius:var(--radius-md); font-size:var(--text-xs); font-weight:var(--weight-semibold); cursor:pointer; transition:all var(--transition-fast); font-family:var(--font-body); white-space:nowrap; }
        .au-btn:disabled { opacity:0.4; cursor:not-allowed; }
        .au-btn--grant  { background:var(--gold-100);     color:var(--gold-900); }
        .au-btn--grant:hover:not(:disabled)  { background:var(--gold-200); }
        .au-btn--revoke { background:var(--warning-light); color:var(--warning); }
        .au-btn--revoke:hover:not(:disabled) { background:#fde68a; }
        .au-btn--delete { background:var(--error-light);   color:var(--error); }
        .au-btn--delete:hover:not(:disabled) { background:#fecaca; }
        .au-center { text-align:center; padding:var(--space-12); color:var(--neutral-500); }
      `}</style>
    </AdminLayout>
  );
};

export default AdminUsers;